import { useState, useEffect, useCallback } from 'react';
import { Alert } from 'react-native';
import { useRouter } from 'expo-router';
import { authApi } from '../shared/utils/api';
import { PropertyDetail, PropertyPartner } from '../shared/types/property';
import { FinancialEntry } from '../shared/types/financial';
import { PortfolioPartner } from '../shared/types/portfolio';
import { getDisplayName } from '../shared/utils/user';
import { logger } from '../shared/utils/logger';

export interface UsePropertyDataProps {
  portfolioId: string;
  propertyId: string;
}

export interface PropertyDataState {
  property: PropertyDetail | null;
  loading: boolean;
  portfolioPartners: PortfolioPartner[];
  loadingPartners: boolean;
  financialEntries: FinancialEntry[];
  loadingEntries: boolean;
  checklist: any;
  loadingChecklist: boolean;
  surveys: any[];
  loadingSurveys: boolean;
}

export interface PropertyDataActions {
  loadProperty: () => Promise<void>;
  loadFinancialEntries: () => Promise<void>;
  loadPortfolioPartners: () => Promise<void>;
  loadChecklist: () => Promise<void>;
  loadSurveys: () => Promise<void>;
  updateProperty: (data: {
    street_address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    county?: string;
    latitude?: number | null;
    longitude?: number | null;
    acreage?: number | null;
    property_value?: number | null;
    zillow_url?: string | null;
    status?: PropertyDetail['status'];
  }) => Promise<boolean>;
  deleteProperty: () => Promise<boolean>;
  addPartner: (userId: number, ownershipPercentage: number) => Promise<boolean>;
  updatePartnerOwnership: (userId: number, ownershipPercentage: number) => Promise<boolean>;
  removePartner: (partner: PropertyPartner) => Promise<boolean>;
  addFinancialEntry: (entry: any) => Promise<boolean>;
  updateFinancialEntry: (entryId: number, data: any) => Promise<boolean>;
  deleteFinancialEntry: (entryId: number) => Promise<boolean>;
  createSurvey: (phase: 'onset' | 'intermediate' | 'final') => Promise<any>;
  addSurveyItem: (surveyId: number, text: string) => Promise<boolean>;
  toggleSurveyVote: (surveyId: number, itemId: number) => Promise<boolean>;
  deleteSurveyItem: (surveyId: number, itemId: number) => Promise<boolean>;
}

export interface UsePropertyDataReturn extends PropertyDataState, PropertyDataActions {
  availablePartners: PortfolioPartner[];
  totalOwnership: number;
  grossIncome: number;
  totalExpenses: number;
  netCashFlow: number;
}

export function usePropertyData({ portfolioId, propertyId }: UsePropertyDataProps): UsePropertyDataReturn {
  const router = useRouter();
  
  // State
  const [property, setProperty] = useState<PropertyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [portfolioPartners, setPortfolioPartners] = useState<PortfolioPartner[]>([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [financialEntries, setFinancialEntries] = useState<FinancialEntry[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [checklist, setChecklist] = useState<any>(null);
  const [loadingChecklist, setLoadingChecklist] = useState(false);
  const [surveys, setSurveys] = useState<any[]>([]);
  const [loadingSurveys, setLoadingSurveys] = useState(false);

  // Parse IDs
  const portId = parseInt(portfolioId, 10);
  const propId = parseInt(propertyId, 10);

  // Load property
  const loadProperty = useCallback(async () => {
    if (isNaN(portId) || isNaN(propId)) {
      Alert.alert('Error', 'Invalid portfolio or property ID');
      router.replace('/home');
      return;
    }

    try {
      setLoading(true);
      const response = await authApi.getProperty(portId, propId);
      setProperty(response.property);
    } catch (error: any) {
      if (error.status === 403) {
        Alert.alert('Access Denied', 'You do not have access to this property.');
      } else if (error.status === 404) {
        Alert.alert('Not Found', 'Property not found.');
      } else {
        Alert.alert('Error', error.message || 'Failed to load property');
      }
      router.push(`/portfolios/${portfolioId}`);
    } finally {
      setLoading(false);
    }
  }, [portId, propId, portfolioId, router]);

  // Load portfolio partners
  const loadPortfolioPartners = useCallback(async () => {
    if (isNaN(portId)) return;

    try {
      setLoadingPartners(true);
      const response = await authApi.getPortfolio(portId);
      setPortfolioPartners(response.portfolio.partners);
    } catch (error: any) {
      logger.error('Failed to load portfolio partners', error, 'property');
    } finally {
      setLoadingPartners(false);
    }
  }, [portId]);

  // Load financial entries
  const loadFinancialEntries = useCallback(async () => {
    if (isNaN(portId) || isNaN(propId)) return;

    try {
      setLoadingEntries(true);
      const response = await authApi.getFinancialEntries(portId, propId);
      setFinancialEntries(response.entries);
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to load financial entries');
    } finally {
      setLoadingEntries(false);
    }
  }, [portId, propId]);

  // Load checklist
  const loadChecklist = useCallback(async () => {
    if (isNaN(portId) || isNaN(propId)) return;

    try {
      setLoadingChecklist(true);
      const response = await authApi.getDueDiligenceChecklist(portId, propId);
      setChecklist(response.checklist);
    } catch (error: any) {
      logger.error('Failed to load checklist', error, 'property');
    } finally {
      setLoadingChecklist(false);
    }
  }, [portId, propId]);

  // Load surveys
  const loadSurveys = useCallback(async () => {
    if (isNaN(portId) || isNaN(propId)) return;

    try {
      setLoadingSurveys(true);
      const response = await authApi.getPropertyPotentialSurveys(portId, propId);
      setSurveys(response.surveys || []);
    } catch (error: any) {
      logger.error('Failed to load surveys', error, 'property');
    } finally {
      setLoadingSurveys(false);
    }
  }, [portId, propId]);

  // Update property
  const updateProperty = useCallback(async (data: {
    street_address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    county?: string;
    latitude?: number | null;
    longitude?: number | null;
    acreage?: number | null;
    property_value?: number | null;
    zillow_url?: string | null;
    status?: PropertyDetail['status'];
  }): Promise<boolean> => {
    if (!property) return false;

    try {
      await authApi.updateProperty(portId, propId, data);
      Alert.alert('Success', 'Property updated successfully!');
      await loadProperty();
      return true;
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to update property');
      return false;
    }
  }, [property, portId, propId, loadProperty]);

  // Delete property
  const deleteProperty = useCallback(async (): Promise<boolean> => {
    if (!property) return false;

    return new Promise((resolve) => {
      Alert.alert(
        'Delete Property',
        `Are you sure you want to delete this property? This action cannot be undone.`,
        [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await authApi.deleteProperty(portId, propId);
                Alert.alert('Success', 'Property deleted successfully.');
                router.push(`/portfolios/${portfolioId}`);
                resolve(true);
              } catch (error: any) {
                Alert.alert('Error', error.message || 'Failed to delete property.');
                resolve(false);
              }
            },
          },
        ]
      );
    });
  }, [property, portId, propId, portfolioId, router]);

  // Add partner
  const addPartner = useCallback(async (userId: number, ownershipPercentage: number): Promise<boolean> => {
    if (!property) return false;

    try {
      await authApi.addPropertyPartner(portId, propId, {
        user_id: userId,
        ownership_percentage: ownershipPercentage,
      });
      Alert.alert('Success', 'Partner added successfully!');
      await loadProperty();
      return true;
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to add partner.');
      return false;
    }
  }, [property, portId, propId, loadProperty]);

  // Update partner ownership
  const updatePartnerOwnership = useCallback(async (userId: number, ownershipPercentage: number): Promise<boolean> => {
    if (!property) return false;

    try {
      await authApi.updatePropertyPartnerOwnership(portId, propId, userId, {
        ownership_percentage: ownershipPercentage,
      });
      Alert.alert('Success', 'Ownership updated successfully!');
      await loadProperty();
      return true;
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to update ownership.');
      return false;
    }
  }, [property, portId, propId, loadProperty]);

  // Remove partner
  const removePartner = useCallback(async (partner: PropertyPartner): Promise<boolean> => {
    if (!property) return false;

    return new Promise((resolve) => {
      Alert.alert(
        'Remove Partner',
        `Are you sure you want to remove ${getDisplayName(partner)} from this property?`,
        [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          {
            text: 'Remove',
            style: 'destructive',
            onPress: async () => {
              try {
                await authApi.removePropertyPartner(portId, propId, partner.user_id);
                Alert.alert('Success', `${getDisplayName(partner)} removed successfully.`);
                await loadProperty();
                resolve(true);
              } catch (error: any) {
                Alert.alert('Error', error.message || 'Failed to remove partner.');
                resolve(false);
              }
            },
          },
        ]
      );
    });
  }, [property, portId, propId, loadProperty]);

  // Add financial entry
  const addFinancialEntry = useCallback(async (entry: any): Promise<boolean> => {
    try {
      await authApi.createFinancialEntry(portId, propId, entry);
      Alert.alert('Success', 'Entry added successfully!');
      await loadFinancialEntries();
      return true;
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to add entry.');
      return false;
    }
  }, [portId, propId, loadFinancialEntries]);

  // Update financial entry
  const updateFinancialEntry = useCallback(async (entryId: number, data: any): Promise<boolean> => {
    try {
      await authApi.updateFinancialEntry(portId, propId, entryId, data);
      Alert.alert('Success', 'Entry updated successfully!');
      await loadFinancialEntries();
      return true;
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to update entry.');
      return false;
    }
  }, [portId, propId, loadFinancialEntries]);

  // Delete financial entry
  const deleteFinancialEntry = useCallback(async (entryId: number): Promise<boolean> => {
    return new Promise((resolve) => {
      Alert.alert(
        'Delete Entry',
        'Are you sure you want to delete this financial entry?',
        [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await authApi.deleteFinancialEntry(portId, propId, entryId);
                Alert.alert('Success', 'Entry deleted successfully.');
                await loadFinancialEntries();
                resolve(true);
              } catch (error: any) {
                Alert.alert('Error', error.message || 'Failed to delete entry.');
                resolve(false);
              }
            },
          },
        ]
      );
    });
  }, [portId, propId, loadFinancialEntries]);

  // Create survey
  const createSurvey = useCallback(async (phase: 'onset' | 'intermediate' | 'final'): Promise<any> => {
    try {
      const response = await authApi.createPropertyPotentialSurvey(portId, propId, {
        phase,
      });
      await loadSurveys();
      return response.survey;
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to create survey.');
      return null;
    }
  }, [portId, propId, loadSurveys]);

  // Add survey item
  const addSurveyItem = useCallback(async (surveyId: number, text: string): Promise<boolean> => {
    try {
      await authApi.createSurveyItem(portId, propId, surveyId, { item_text: text });
      await loadSurveys();
      return true;
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to add item.');
      return false;
    }
  }, [portId, propId, loadSurveys]);

  // Toggle survey vote
  const toggleSurveyVote = useCallback(async (surveyId: number, itemId: number): Promise<boolean> => {
    try {
      await authApi.toggleSurveyItemVote(portId, propId, surveyId, itemId);
      await loadSurveys();
      return true;
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to toggle vote.');
      return false;
    }
  }, [portId, propId, loadSurveys]);

  // Delete survey item
  const deleteSurveyItem = useCallback(async (surveyId: number, itemId: number): Promise<boolean> => {
    return new Promise((resolve) => {
      Alert.alert(
        'Delete Item',
        'Are you sure you want to delete this survey item?',
        [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await authApi.deleteSurveyItem(portId, propId, surveyId, itemId);
                await loadSurveys();
                resolve(true);
              } catch (error: any) {
                Alert.alert('Error', error.message || 'Failed to delete item.');
                resolve(false);
              }
            },
          },
        ]
      );
    });
  }, [portId, propId, loadSurveys]);

  // Initial data load
  useEffect(() => {
    if (portfolioId && propertyId) {
      loadProperty();
      loadFinancialEntries();
      loadPortfolioPartners();
    }
  }, [portfolioId, propertyId]);

  // Load due diligence data when property is in research
  useEffect(() => {
    if (property?.status === 'research') {
      loadChecklist();
      loadSurveys();
    }
  }, [property?.status]);

  // Computed values
  const availablePartners = portfolioPartners.filter(
    portfolioPartner => !property?.partners.some(propPartner => propPartner.user_id === portfolioPartner.user_id)
  );

  const totalOwnership = property?.partners.reduce((sum, p) => sum + (p.ownership_percentage || 0), 0) || 0;

  const grossIncome = financialEntries
    .filter(e => e.type === 'income')
    .reduce((sum, e) => sum + e.amount, 0);

  const totalExpenses = financialEntries
    .filter(e => e.type === 'expense')
    .reduce((sum, e) => sum + e.amount, 0);

  const netCashFlow = grossIncome - totalExpenses;

  return {
    // State
    property,
    loading,
    portfolioPartners,
    loadingPartners,
    financialEntries,
    loadingEntries,
    checklist,
    loadingChecklist,
    surveys,
    loadingSurveys,
    // Actions
    loadProperty,
    loadFinancialEntries,
    loadPortfolioPartners,
    loadChecklist,
    loadSurveys,
    updateProperty,
    deleteProperty,
    addPartner,
    updatePartnerOwnership,
    removePartner,
    addFinancialEntry,
    updateFinancialEntry,
    deleteFinancialEntry,
    createSurvey,
    addSurveyItem,
    toggleSurveyVote,
    deleteSurveyItem,
    // Computed
    availablePartners,
    totalOwnership,
    grossIncome,
    totalExpenses,
    netCashFlow,
  };
}

